import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { useActiveOrganization } from "@/hooks/organizations";
import { Pagination } from "@/types";
import {
  OrganizationMembersActiveRow,
  OrganizationMembersTableRow,
  isOrganizationMembersActiveRow,
} from "@/types/organization-member";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";

export const fetchOrganizationMembers = async ({
  userToken,
  organizationId,
  page,
  perPage,
  search,
  workspaceIds,
}: {
  userToken: string;
  organizationId: number;
  page: number;
  perPage: number;
  search?: string;
  workspaceIds?: number[];
}): Promise<Pagination<OrganizationMembersTableRow>> => {
  const url = new URL(ENDPOINTS.organization_members);
  url.searchParams.append("organization_id", organizationId.toString());
  url.searchParams.append("per_page", perPage.toString());
  url.searchParams.append("page", page.toString());
  if (search?.trim()) {
    url.searchParams.append("email", search.trim());
  }
  workspaceIds?.forEach(workspaceId => {
    url.searchParams.append("workspace_ids[]", workspaceId.toString());
  });
  const response = await fetch(url, {
    headers: jsonAuthHeaders(userToken),
  });
  return handleResponse(response);
};

const useActiveOrgMembers = ({
  perPage = 250,
  page = 1,
  organization_id,
  search,
}: {
  perPage?: number;
  page?: number;
  organization_id?: number;
  search?: string;
}) => {
  const { data: activeOrg } = useActiveOrganization();
  const userToken = useUserToken();
  const url = new URL(ENDPOINTS.organization_members);
  if (activeOrg && !organization_id) {
    url.searchParams.append("organization_id", activeOrg.id.toString());
  }
  if (organization_id) {
    url.searchParams.append("organization_id", organization_id.toString());
  }
  url.searchParams.append("per_page", perPage.toString());
  url.searchParams.append("page", page.toString());
  if (search?.trim()) {
    url.searchParams.append("email", search.trim());
  }

  const effectiveOrgId = organization_id ?? activeOrg?.id;
  return useQuery(
    [ENDPOINTS.organization_members, effectiveOrgId, page, perPage, search?.trim() ?? ""],
    (): Promise<Pagination<OrganizationMembersTableRow>> =>
      fetch(url, {
        headers: jsonAuthHeaders(userToken),
      }).then(handleResponse),
    {
      enabled: !!effectiveOrgId,
    },
  );
};

export const getActiveOrganizationMembers = (
  items: OrganizationMembersTableRow[] | undefined,
): OrganizationMembersActiveRow[] =>
  (items ?? []).filter(
    (item): item is OrganizationMembersActiveRow =>
      isOrganizationMembersActiveRow(item) && item.status === "active",
  );

const useDeleteOrgMember = () => {
  const queryClient = useQueryClient();
  const userToken = useUserToken();
  return useMutation(
    (organizationMemberId: number) =>
      fetch(`${ENDPOINTS.organization_members}/${organizationMemberId}`, {
        method: "DELETE",
        headers: jsonAuthHeaders(userToken),
      }),
    {
      onSuccess: async response => {
        if (response.ok) {
          queryClient.invalidateQueries([ENDPOINTS.organization_members]);
          displayToast("Member removed successfully", ToastType.success);
        } else {
          const data = await response.json();
          displayErrorToast(data.message);
        }
      },
    },
  );
};

const usePatchOrgMember = () => {
  const queryClient = useQueryClient();
  const userToken = useUserToken();
  return useMutation(
    ({
      organizationMemberId,
      organizationRoleId,
    }: {
      organizationMemberId: number;
      organizationRoleId: number;
    }) =>
      fetch(`${ENDPOINTS.organization_members}/${organizationMemberId}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({ organization_role_id: organizationRoleId }),
      }),
    {
      onSuccess: async response => {
        if (response.ok) {
          queryClient.invalidateQueries([ENDPOINTS.organization_members]);
          queryClient.invalidateQueries([ENDPOINTS.legacy_workspace_members]);
          displayToast("Member role updated successfully", ToastType.success);
        } else {
          const data = await response.json();
          displayErrorToast(data.message);
        }
      },
    },
  );
};

export { useActiveOrgMembers, useDeleteOrgMember, usePatchOrgMember };
